import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { BRAND_NAME } from '@/lib/brand'

export type ValuePanel = {
  headline_accent: string
  headline_rest: string
  benefits: string[]
  proof: string
}

function KegMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} fill="none" aria-hidden="true">
      <path d="M4 2.5h8l-.6 10.2a1.5 1.5 0 0 1-1.5 1.4H6.1a1.5 1.5 0 0 1-1.5-1.4L4 2.5Z" fill="currentColor" />
      <path d="M4.2 6.2h7.6" stroke="#E2461F" strokeWidth="1.5" />
    </svg>
  )
}

function Wordmark({ dark = false }: { dark?: boolean }) {
  return (
    <Link
      href="/"
      className={`inline-flex items-center gap-2.5 text-xl font-extrabold tracking-tight ${dark ? 'text-paper' : 'text-ink'}`}
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${dark ? 'bg-paper text-ink' : 'bg-ink text-paper'}`}>
        <KegMark className="h-4 w-4" />
      </span>
      {BRAND_NAME}
    </Link>
  )
}

function Headline({ panel, dark }: { panel: ValuePanel; dark?: boolean }) {
  return (
    <>
      <span className="font-script text-flame">{panel.headline_accent}</span>{' '}
      <span className={dark ? 'text-paper' : 'text-ink'}>{panel.headline_rest}</span>
    </>
  )
}

// Public/marketing shell for auth pages (login, signup, beta, password reset):
// a dark warm value panel on the left sells the product, the form sits on the
// right. Below lg the panel collapses to a compact brand header above the form.
export function AuthShell({ valuePanel, children }: { valuePanel: ValuePanel; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-paper lg:grid lg:grid-cols-2">
      <aside className="hidden flex-col justify-between bg-ink p-12 lg:flex">
        <Wordmark dark />
        <div>
          <h2 className="font-display text-5xl leading-[1.05]">
            <Headline panel={valuePanel} dark />
          </h2>
          <ul className="mt-8 space-y-4">
            {valuePanel.benefits.map((benefit) => (
              <li key={benefit} className="flex items-start gap-3 text-paper/90">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-flame/20 text-flame">
                  <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={3}>
                    <path d="M4 10l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="text-[15px] leading-snug">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="font-script text-2xl text-mustard">{valuePanel.proof}</p>
      </aside>

      <main className="flex flex-col justify-center px-6 py-12 sm:px-10">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden">
            <Wordmark />
            <p className="mt-4 font-display text-3xl leading-tight">
              <Headline panel={valuePanel} />
            </p>
          </div>
          <div className="mt-8 lg:mt-0">{children}</div>
        </div>
      </main>
    </div>
  )
}
